const ORIGINAL_PMI = 0.0044;
const MORTGAGE_CALCULATOR_PMI = 0.00625;

export default {
    convertLoanTermInMonths(LoanTerm) {
        return LoanTerm * 12;
    },
    convertInterestRatePerMonths(InterestRate) {
        return InterestRate / 1200;
    },
    /*
        amount : total amount balance
        term : months (#yr*12)
        apr : rate/month (rate/12)
    */
    getPayment(amount, term, apr) {
        /* Calculates the monthly payment from annual percentage
        rate, term of loan in months and loan amount. * */
        let acc = 0;
        const base = 1 + apr;
        for (let i = 1; i <= term; i++) {
            acc += base ** -i;
        }
        return amount / acc;
    },
    monthlyPaymentUsingLoanTermInYears(LoanAmount, LoanTerm, InterestRate) {
        return this.getPayment(LoanAmount, this.convertLoanTermInMonths(LoanTerm), this.convertInterestRatePerMonths(InterestRate));
    },
    getMonthsLeft(CurrentInterestRate, CurrentMortgageBalance, CurrentMonthlyPayment) {
        const interestrate = CurrentInterestRate / 1200;
        const monthsleftcalcnumo = -Math.log(1 - interestrate * (CurrentMortgageBalance / CurrentMonthlyPayment));
        const monthsleftcalcdeno = Math.log(1 + interestrate);
        const MonthsLeftOnCurrentLoan = Math.round(monthsleftcalcnumo / monthsleftcalcdeno);
        return isNaN(MonthsLeftOnCurrentLoan) ? 0 : MonthsLeftOnCurrentLoan;
    },
    monthlyPaymentForRefinance(MonthsLeftOnCurrentLoan, CurrentMortgageBalance, CurrentMonthlyPayment, LoanTermInYears, NewInterestRate) {
        return this.monthlyPaymentUsingLoanTermInYears(CurrentMortgageBalance, LoanTermInYears, NewInterestRate);
    },
    getIntrestRate(termmonth, monthlypayment, currentbalance) {
        // Formula:- initialGuess = (2 (N * P - C)) / (N * C)
        const initialguess = 2 * (termmonth * monthlypayment - currentbalance) / (termmonth * currentbalance);
        const interestRate = this.getFinalIntrestRate(initialguess, termmonth, monthlypayment, currentbalance);
        return interestRate * 100;
    },
    getFinalIntrestRate(interestRateValue, termmonth, monthlypayment, currentbalance) {
        let newinterestrate = 0;
        let guess = 0;
        let interestRate = interestRateValue;

        while (Math.round(interestRate * 100, 5) !== Math.round(newinterestrate, 5) && guess < 20) {
            guess++;
            newinterestrate = interestRate;
            // Formula:-  CurrentGuess = Guess - ( (P - ( P * ( (1 + Guess )^-N ) ) - (Guess * C) ) / ( ( N * P * ( (1 + Guess) ^ (-N -1) ) ) - C )

            interestRate = newinterestrate - (monthlypayment - monthlypayment * (1 + newinterestrate) ** -termmonth - newinterestrate * currentbalance) / (termmonth * monthlypayment * (1 + newinterestrate) ** (-termmonth - 1) - currentbalance);
        }
        return interestRate;
    },
    minimumPayment(LoanAmount, InterestRate) {
        return this.convertInterestRatePerMonths(InterestRate) * LoanAmount;
    },
    getHomeEquityLoan(currentAppraisedHomeValue, amountOwe) {
        return Math.round(0.80 * currentAppraisedHomeValue - amountOwe);
    },
    compute_PMT(principle, rate, months) {
        return principle * rate / (1 - (1 + rate) ** (-1 * months)) * 100 / 100;
    },
    compute_PMI(ltv, amount) {
        let pmi = 0;
        if (ltv >= 0.8) {
            pmi = amount * ORIGINAL_PMI / 12;
        }
        return pmi;
    },
    computePMI(amount) {
        return this.isNumberOrElse(amount) * MORTGAGE_CALCULATOR_PMI / 12;
    },
    isNumberOrElse(amount) {
        return Number.isNaN(amount) ? 0 : amount;
    },
    calculateHomeRates(data, type) {
        if (!type) return {};

        const calculatedData = {};

        const annualIncome = this.isNumberOrElse(parseInt(data.annualIncome, 10)) / 12;
        const incomeTaxes = Math.round(annualIncome * 0.3);
        const monthlyExpenses = this.isNumberOrElse(parseInt(data.monthlyExpenses, 10));
        const loanTerm = this.isNumberOrElse(parseInt(data.loanterm, 10)) * 12;
        const downPayment = this.isNumberOrElse(parseInt(data.estimatedDownPayment, 10));
        const HOA = this.isNumberOrElse(parseInt(data.hoa, 10)) / 12;
        const HOI = this.isNumberOrElse(parseInt(data.homeInsurance, 10)) / 12;
        const mortgageRate = this.isNumberOrElse(parseFloat(data.mortgageRate)) / 1200;
        const propertyTaxesAPR = this.isNumberOrElse(parseFloat(data.propertyTaxes)) / 1200;
        const propertyTaxes = this.isNumberOrElse(parseFloat(data.propertyTaxes));
        const debtToIncomeRatio = this.isNumberOrElse(parseInt(data.debtToIncome, 10)) / 100;

        const fixedCosts = monthlyExpenses + HOA + HOI;

        const mortgagePayment = annualIncome * debtToIncomeRatio - fixedCosts;

        const factor = ((1 + mortgageRate) ** loanTerm - 1) / ((1 + mortgageRate) ** loanTerm * mortgageRate);

        // Without pmiRrate
        const homePriceEstimate = (mortgagePayment * factor + downPayment) / (1 + factor * propertyTaxesAPR);
        const loanAmountEstimate = homePriceEstimate - downPayment;

        const pmiRrate = data.pmi ? (loanAmountEstimate / homePriceEstimate) * MORTGAGE_CALCULATOR_PMI / 12 : 0;

        const homePrice = (mortgagePayment * factor + downPayment) / (1 + factor * (propertyTaxesAPR + pmiRrate));

        const loanAmount = homePrice - downPayment;

        const propertyTaxeAmount = homePrice * propertyTaxesAPR;

        const PMI = this.computePMI(loanAmount);

        const PMT = this.compute_PMT(loanAmount, mortgageRate, loanTerm);

        const totalMonthlyPayment = PMT + PMI + HOA + HOI + propertyTaxeAmount;

        const leftOver = annualIncome - totalMonthlyPayment - incomeTaxes - monthlyExpenses;

        switch (type) {
            case 'calculate':
                if (mortgagePayment < 0 || totalMonthlyPayment > annualIncome) {
                    calculatedData.payment = 0;
                    calculatedData.loanamount = '$___,___';
                    calculatedData.homeprice = '$___,___';
                    calculatedData.isValid = false;
                } else {
                    calculatedData.payment = Math.round(totalMonthlyPayment);
                    calculatedData.loanamount = Math.round(loanAmount);
                    calculatedData.homeprice = Math.round(homePrice);
                    calculatedData.isValid = true;
                }
                calculatedData.debts = Math.round(monthlyExpenses);
                calculatedData.leftover = Math.round(leftOver);
                calculatedData.taxes = Math.round(incomeTaxes);
                calculatedData.income = Math.round(annualIncome);
                calculatedData.pmi = Math.round(PMI);
                return calculatedData;

            case 'calculateDownpayment':
                return Math.trunc(downPayment / homePrice * 100);

            case 'calculatePropertyTaxval':
                return Math.round(homePrice * propertyTaxes / 100);

            default:
                return calculatedData;
        }
    },
    calculateAprRates(data) {
        const calculatedData = {};
        const actualinterestrate = data.interestRate / 100;
        const calculatepow = (1 + actualinterestrate / 365.25) ** (data.loanterm * 365.25);
        const calculatepayment = data.loanAmount / ((1 - 1 / calculatepow) / (actualinterestrate / data.payBack));
        const totalpayment = calculatepayment * data.loanterm * data.payBack;
        const totalinterestvalue = totalpayment - data.loanAmount;
        const totalpaymentwithfees = totalpayment + data.financeFees;
        const amountfinanced = data.loanAmount + data.originationFees + data.financeFees;
        const upfrontpocketfees = data.originationFees + data.financeFees;
        calculatedData.totalinterestvalue = Math.round(totalinterestvalue);
        calculatedData.totalpaymentwithfees = totalpaymentwithfees;
        calculatedData.amountfinanced = amountfinanced;
        calculatedData.upfrontpocketfees = Math.round(upfrontpocketfees);
        calculatedData.calculatepayment = calculatepayment;
        calculatedData.totalpayment = totalpayment;
        calculatedData.totalamount = Math.round(data.loanAmount);
        calculatedData.period = data.loanterm * data.payBack;
        calculatedData.payBack = data.payBack;
        calculatedData.originationFees = data.originationFees;
        calculatedData.financeFees = data.financeFees;
        calculatedData.principalpercentage = Math.round(100 * data.loanAmount / totalpaymentwithfees);
        calculatedData.interestpercentage = Math.round(100 * totalinterestvalue / totalpaymentwithfees);
        calculatedData.feespercentage = Math.round(100 * upfrontpocketfees / totalpaymentwithfees);
        calculatedData.actualinterestrate = actualinterestrate;
        return calculatedData;
    },
    getMonthlyPrincipalAndIntreset(mortgageAmount, amortizationPeriodInMonths, monthlyInterestRatePerc) {
        return mortgageAmount * (monthlyInterestRatePerc * (1 + monthlyInterestRatePerc) ** amortizationPeriodInMonths / ((1 + monthlyInterestRatePerc) ** amortizationPeriodInMonths - 1));
    },
    CUMIPMT(loanAmount, end, mortgagePayment, rate) {
        const payment = mortgagePayment;
        let interest = 0.0;
        let totalinterest = 0.0;
        let balance = loanAmount;
        let principal = 0.0;
        for (let i = 1; i <= end; i++) {
            interest = balance * rate;
            totalinterest += interest;
            principal = payment - interest;
            balance -= principal;
        }
        return Math.round(totalinterest);
    },
};
